import React, { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import AttachMoneyOutlined from '@mui/icons-material/AttachMoneyOutlined';
import Whatshot from '@mui/icons-material/Whatshot';
import ApartmentOutlined from '@mui/icons-material/ApartmentOutlined';
import TrendingUp from '@mui/icons-material/TrendingUp';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import ExploreOutlined from '@mui/icons-material/ExploreOutlined';
import LocationCityOutlined from '@mui/icons-material/LocationCityOutlined';
import InsightsChart from 'components/pages/area-listings/components/market-insights/insights-chart';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { buildClassName } from 'utils/build-class-name';
import PriceTiles from './components/price-tiles';
import SellListGauge from './components/sell-list-gauge';
import TypeBreakdownBars from './components/type-breakdown-bars';
import TopNeighbourhoodsBars from './components/top-neighbourhoods-bars';
import NearbyCitiesPills from './components/nearby-cities-pills';
import styles from './style.module.scss';

import type { InsightsResponse } from '@zoocasa/go-search';
import type { NearbyCity } from './components/nearby-cities-pills';

export interface StaticFaqSection {
  question: string;
  paragraphs: readonly string[];
}

export interface MarketFaqAccordionProps {
  areaName?: string;
  insightsData?: InsightsResponse;
  nearbyCities?: readonly NearbyCity[];
  /** City's own info-page slug (e.g. "calgary-ab") — enables neighbourhood row links. */
  citySlug?: string;
  /** Hand-written sections (no live data) inserted right after the price question — currently Toronto-only. */
  staticSections?: readonly StaticFaqSection[];
}

type AccentKey = 'gold' | 'red' | 'purple' | 'blue' | 'green';

const ACCENT_ICON_CLASS: Record<AccentKey, string> = {
  gold: styles.iconGold,
  red: styles.iconRed,
  purple: styles.iconPurple,
  blue: styles.iconBlue,
  green: styles.iconGreen,
};

/** Renders `**bold**`-marked static-section text with real `<strong>` tags. */
function renderBoldedText(text: string): React.ReactNode {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i}>{part.slice(2, -2)}</strong>
      : part
  );
}

/**
 * Market FAQ — an accordion where each answer is a live market-stat visual
 * (price tiles, sale/list gauge, property-type breakdown, price trend chart,
 * top neighbourhoods, nearby cities), all derived from InsightsResponse.
 * Skips any question whose backing data is missing, same live-data-only
 * philosophy as the hybrid area-faq component. Custom-built (not the shared
 * MUI-based CollapsibleSection) to match the design's floating rounded cards
 * with per-question accent colors — the shared primitive's flat divider-list
 * styling can't be overridden for this look without fighting its `!important`
 * rules.
 */
const MarketFaqAccordion = ({ areaName, insightsData, nearbyCities, citySlug, staticSections }: MarketFaqAccordionProps) => {
  const { t } = useTranslation(Namespaces.COMMON);
  const { formatCurrency, formatNumber } = useI18nFormatting();

  const area = areaName?.trim() || '';

  const items = useMemo(() => {
    if (!area) {
      return [];
    }

    const medianPurchasePrice = insightsData?.MedianPurchasePrice ?? 0;
    const medianRentPrice = insightsData?.MedianRentPrice ?? 0;
    const daysOnMarket = insightsData?.DaysOnMarket ?? 0;
    const sellListRatio = insightsData?.SellListRatio ?? 0;
    const totalHouses = insightsData?.TotalHouses ?? 0;
    const totalTownhouses = insightsData?.TotalTownhouses ?? 0;
    const totalCondos = insightsData?.TotalCondos ?? 0;
    const totalRentals = insightsData?.TotalRentals ?? 0;
    const topNeighbourhoods = insightsData?.topNeighbourhoods ?? [];
    const medianPriceTrends = insightsData?.medianPriceTrends ?? [];

    const list: { question: string; answerText: string; icon: React.ReactNode; accent: AccentKey; content: React.ReactNode }[] = [];

    if (medianPurchasePrice > 0 || medianRentPrice > 0 || daysOnMarket > 0) {
      const parts: string[] = [];
      if (medianPurchasePrice > 0) parts.push(`the median home price is ${formatCurrency(medianPurchasePrice)}`);
      if (medianRentPrice > 0) parts.push(`median rent is ${formatCurrency(medianRentPrice)}`);
      if (daysOnMarket > 0) parts.push(`homes typically sell in ${formatNumber(daysOnMarket)} days`);
      list.push({
        question: t(LOCALE_COMMON_PATHS.MARKETFAQ_Q1_QUESTION, { area, defaultValue: 'How much does it cost to buy or rent in {{area}}?' }),
        answerText: `In ${area}, ${parts.join(', ')}.`,
        icon: <AttachMoneyOutlined />,
        accent: 'gold',
        content: <PriceTiles medianPurchasePrice={medianPurchasePrice} medianRentPrice={medianRentPrice} daysOnMarket={daysOnMarket} />,
      });
    }

    if (sellListRatio > 0) {
      const pct = formatNumber(sellListRatio * 100, { maximumFractionDigits: 1 });
      const domClause = daysOnMarket > 0 ? `, in about ${formatNumber(daysOnMarket)} days` : '';
      list.push({
        question: t(LOCALE_COMMON_PATHS.MARKETFAQ_Q2_QUESTION, { area, defaultValue: "Is {{area}} a buyer's or seller's market?" }),
        answerText: `${area} homes sell for about ${pct}% of asking price on average${domClause}.`,
        icon: <Whatshot />,
        accent: 'red',
        content: <SellListGauge sellListRatio={sellListRatio} daysOnMarket={daysOnMarket} areaName={area} />,
      });
    }

    if (totalHouses > 0 || totalTownhouses > 0 || totalCondos > 0 || totalRentals > 0) {
      const typeParts: string[] = [];
      if (totalHouses > 0) typeParts.push(`${formatNumber(totalHouses)} houses`);
      if (totalTownhouses > 0) typeParts.push(`${formatNumber(totalTownhouses)} townhouses`);
      if (totalCondos > 0) typeParts.push(`${formatNumber(totalCondos)} condos`);
      if (totalRentals > 0) typeParts.push(`${formatNumber(totalRentals)} rentals`);
      list.push({
        question: t(LOCALE_COMMON_PATHS.MARKETFAQ_Q3_QUESTION, { area, defaultValue: 'What homes are for sale in {{area}}?' }),
        answerText: `${area} currently has ${typeParts.join(', ')} for sale or rent.`,
        icon: <ApartmentOutlined />,
        accent: 'purple',
        content: (
          <TypeBreakdownBars
            areaName={area}
            totalHouses={totalHouses}
            totalTownhouses={totalTownhouses}
            totalCondos={totalCondos}
            totalRentals={totalRentals}
            avgHousePrice={insightsData?.AvgHousePrice ?? 0}
            avgTownhousePrice={insightsData?.AvgTownhousePrice ?? 0}
            avgCondoPrice={insightsData?.AvgCondoPrice ?? 0}
            avgRentalPrice={insightsData?.AvgRentalPrice ?? 0}
          />
        ),
      });
    }

    if (medianPriceTrends.length > 0) {
      const validTrends = medianPriceTrends.filter(point => point?.medianPrice > 0);
      let trendAnswer = `See the median home price trend for ${area} over the past 12 months.`;
      if (validTrends.length >= 2) {
        const latest = validTrends[validTrends.length - 1];
        const earliest = validTrends[0];
        const changePct = ((latest.medianPrice - earliest.medianPrice) / earliest.medianPrice) * 100;
        const direction = changePct >= 1 ? 'risen' : changePct <= -1 ? 'fallen' : 'stayed roughly flat';
        const magnitude = Math.abs(changePct) >= 1 ? ` about ${formatNumber(Math.abs(changePct), { maximumFractionDigits: 1 })}%` : '';
        trendAnswer = `Over this period, the median home price in ${area} has ${direction}${magnitude}, to ${formatCurrency(latest.medianPrice)}.`;
      }
      list.push({
        question: t(LOCALE_COMMON_PATHS.MARKETFAQ_Q4_QUESTION, { area, defaultValue: 'Are home prices in {{area}} rising or falling?' }),
        answerText: trendAnswer,
        icon: <TrendingUp />,
        accent: 'blue',
        content: <InsightsChart insightsData={insightsData as InsightsResponse} hideTitle />,
      });
    }

    if (topNeighbourhoods.length > 0) {
      const topNames = topNeighbourhoods.filter(n => n?.name).slice(0, 3).map(n => n.name);
      list.push({
        question: t(LOCALE_COMMON_PATHS.MARKETFAQ_Q5_QUESTION, { area, defaultValue: 'Which {{area}} neighbourhoods have the most listings?' }),
        answerText: `The most active neighbourhoods in ${area} by listing volume are ${topNames.join(', ')}.`,
        icon: <PlaceOutlined />,
        accent: 'green',
        content: <TopNeighbourhoodsBars topNeighbourhoods={topNeighbourhoods} citySlug={citySlug} />,
      });
    }

    if (nearbyCities && nearbyCities.length > 0) {
      const nearbyNames = nearbyCities.slice(0, 5).map(city => city.name);
      list.push({
        question: t(LOCALE_COMMON_PATHS.MARKETFAQ_Q6_QUESTION, { area, defaultValue: 'What are the most popular cities in {{area}} to live in??' }),
        answerText: `Cities and areas near ${area} include ${nearbyNames.join(', ')}.`,
        icon: <ExploreOutlined />,
        accent: 'blue',
        content: <NearbyCitiesPills cities={nearbyCities} />,
      });
    }

    if (staticSections && staticSections.length > 0) {
      // Right after the price question (index 0 when present), matching where it was requested.
      const insertAt = list.length > 0 ? 1 : 0;
      list.splice(insertAt, 0, ...staticSections.map(section => ({
        question: section.question,
        answerText: section.paragraphs.join(' ').replace(/\*\*/g, ''),
        icon: <LocationCityOutlined />,
        accent: 'blue' as AccentKey,
        content: (
          <div className={styles.staticProse}>
            {section.paragraphs.map((paragraph, i) => <p key={i}>{renderBoldedText(paragraph)}</p>)}
          </div>
        ),
      })));
    }

    return list;
  }, [area, insightsData, nearbyCities, citySlug, staticSections, t, formatCurrency, formatNumber]);

  const [openIndexes, setOpenIndexes] = useState<Record<number, boolean>>({ 0: true });
  const toggle = (index: number) => setOpenIndexes(prev => ({ ...prev, [index]: !prev[index] }));

  if (items.length === 0) {
    return null;
  }

  const heading = t(LOCALE_COMMON_PATHS.MARKETFAQ_HEADING, { area, defaultValue: '{{area}} Real Estate FAQ' });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answerText,
      },
    })),
  };

  return (
    <section className={styles.component} id="market-faq" aria-label={heading}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className={styles.list}>
        {items.map((item, index) => {
          const isOpen = !!openIndexes[index];
          const buttonId = `market-faq-button-${index}`;
          const panelId = `market-faq-panel-${index}`;
          return (
            <div key={index} className={buildClassName(styles.row, isOpen && styles.rowOpen)}>
              <h3 className={styles.rowHeading}>
                <button
                  type="button"
                  id={buttonId}
                  className={styles.rowHeader}
                  onClick={() => toggle(index)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                >
                  <span className={buildClassName(styles.questionIcon, ACCENT_ICON_CLASS[item.accent])}>{item.icon}</span>
                  <span className={styles.questionText}>{item.question}</span>
                  <span className={styles.toggleIcon}>
                    <span className={styles.toggleBarH} />
                    <span className={buildClassName(styles.toggleBarV, isOpen && styles.toggleBarVOpen)} />
                  </span>
                </button>
              </h3>
              <div
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                aria-hidden={!isOpen}
                inert={!isOpen}
                className={buildClassName(styles.answerWrap, isOpen && styles.answerWrapOpen)}
              >
                <div className={styles.answerInner}>
                  <div className={styles.answerPadding}>
                    {item.content}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default MarketFaqAccordion;
